import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Input,
} from '@angular/core';

import { Subscription } from 'rxjs';

import { IStudentClassListItemDTO } from '@models/student-classroom';
import { ITeacherClassListItemDTO } from '@app/infrastructure/models/teacher-classroom';
import { StudentClassroomService } from '@core/services/api/student-classroom.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormConfig,
  FormField,
  IFormConfig,
} from '@app/infrastructure/models/form/form';
import { SaveCancelButtonConfig } from '@app/infrastructure/models/form/button';
import { IInputField, InputField } from '@app/infrastructure/models/form/input';
import { UntypedFormGroup } from '@angular/forms';
import {
  ChangeUserRequest,
  IChangeUserRequest,
  IUserDTO,
} from '@app/infrastructure/models/user';
import { FormService } from '@app/infrastructure/core/services/form.service';
import { ConfirmDialogConfig } from '@app/infrastructure/models/dialog';
import { ModalService } from '@app/infrastructure/core/services/modal.service';
import { UserStateService } from '@app/infrastructure/core/services/state/user-state.service';
import { SaveCancelService } from '@app/infrastructure/core/services/state/save-cancel.service';

@Component({
  selector: 'app-shared-classroom-list',
  template: `
    <app-shared-classroom-list-presentation
      [classroomList]="classroomList"
      [headerText]="headerText"
      [subtitle]="subtitle"
      [classroomCode]="codeForm"
      (emitForm)="propagateForm($event)"
      (emitSubmit)="joinClass()"
    ></app-shared-classroom-list-presentation>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedClassroomListSmartComponent implements OnInit, OnDestroy {
  @Input() headerText: string = '';
  @Input() classroomList:
    | IStudentClassListItemDTO[]
    | ITeacherClassListItemDTO[];
  public emitGetClassroomList = new EventEmitter<void>();
  private subscriptions: Subscription[] = [];
  public form: UntypedFormGroup = new UntypedFormGroup({});

  @Input() subtitle?: string;
  @Input() classroomCode = false;

  public codeForm?: FormConfig;
  private user: IUserDTO;

  constructor(
    private activatedRoute: ActivatedRoute,
    private formService: FormService,
    private studentClassroomService: StudentClassroomService,
    private modalService: ModalService,
    private userStateService: UserStateService,
    private saveCancelService: SaveCancelService,
    private router: Router,
  ) {
    this.user = this.activatedRoute.snapshot.data.user;
  }

  public ngOnInit(): void {
    // this.getClassroomList();
    if (this.classroomCode) {
      this.codeForm = this.buildFormConfig();
    }
  }

  private buildFormConfig(): IFormConfig {
    const formConfig = new FormConfig({
      formName: 'form',
      formTitle: 'Join a Class',
      submit: new SaveCancelButtonConfig({
        save: 'Join',
        showCancel: false,
      }),
    });

    const classroomCode = new FormField<IInputField>({
      name: 'classroomCode',
      fieldType: 'input',
      label: 'Classroom Code',
      fieldConfig: new InputField(),
    });
    formConfig.controls.push(classroomCode);

    return formConfig;
  }

  public propagateForm(form: UntypedFormGroup): void {
    this.form = form;
  }

  public joinClass(): void {
    const requestPayload = this.buildPayload();
    this.studentClassroomService
      .checkClassroomCode(requestPayload)
      .subscribe((response) =>
        this.openConfirmDialog(requestPayload, response),
      );
  }

  private openConfirmDialog(
    requestPayload: IChangeUserRequest,
    checkClassroomCodeResponse: {
      teacher: string;
      grade: string;
      className: string;
    },
  ): void {
    const dialogConfig = new ConfirmDialogConfig({
      title: 'Is this your class?',
      messageList: [
        `${checkClassroomCodeResponse.teacher}'s Class`,
        checkClassroomCodeResponse.grade !== 'K'
          ? `${checkClassroomCodeResponse.grade} Grade`
          : `Grade ${checkClassroomCodeResponse.grade}`,
        ,
      ],
      submit: new SaveCancelButtonConfig({
        save: `Yes! This is my class!`,
        cancel: `No! Let's try again`,
      }),
    });
    this.modalService
      .openConfirmDialog(dialogConfig)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.joinClassroom(requestPayload);
          this.form.reset();
        } else {
          this.saveCancelService.setDisabledState(false);
          this.form.reset();
        }
      });
  }

  private joinClassroom(requestPayload: IChangeUserRequest): void {
    this.studentClassroomService
      .joinClassroom(this.user.student?.id, requestPayload)
      .subscribe(() => this.navigateOnEnrolled());
  }

  private navigateOnEnrolled(): void {
    const user = this.userStateService.userSession;
    user.student.isEnrolled = true;
    this.userStateService.setUserSession(user);
    this.router.navigated = false;
    this.router.navigate(['/']);
  }

  public buildPayload(): IChangeUserRequest {
    const payloadDTO = new ChangeUserRequest();
    const payload = this.formService.buildRequestPayload(this.form, payloadDTO);
    payload.inviteCode = this.form.get('classroomCode').value?.toUpperCase();
    return payload;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
